import type { LinkClickSource } from '@/hooks/use-analytics';
import { BAZAK_LINK_HOST } from '@/lib/constants/short-link';

/** Read by the short-link worker, recorded as `position`, and stripped before the redirect. */
export const LINK_POSITION_PARAM = 'bzk_pos';

export type LinkPosition = 'card' | 'carousel';

const POSITION_BY_SOURCE: Partial<Record<LinkClickSource, LinkPosition>> = {
  carousel: 'carousel',
  'flight-card': 'card',
  'vacation-package-card': 'card',
  'organized-trip-card': 'card',
  'reservation-card': 'card',
  'fund-card': 'card',
};

/** No position means no marker, which the worker records as `text`. */
export function linkPositionFor(source: LinkClickSource): LinkPosition | undefined {
  return POSITION_BY_SOURCE[source];
}

function parseShortLink(url: string): URL | undefined {
  try {
    const parsed = new URL(url);
    return parsed.hostname === BAZAK_LINK_HOST ? parsed : undefined;
  } catch {
    return undefined;
  }
}

// Edited as text, not through `searchParams`: that re-encodes every other pair (`%20` becomes `+`),
// so the same link would report a different URL on each click. Each pair's key is still decoded,
// so `%62zk_pos` counts as the marker exactly as `searchParams.has` sees it.
function queryWithoutPosition(shortLink: URL): string {
  return shortLink.search
    .slice(1)
    .split('&')
    .filter(pair => pair && !new URLSearchParams(pair).has(LINK_POSITION_PARAM))
    .join('&');
}

function withQuery({ shortLink, query }: { shortLink: URL; query: string }): string {
  return `${shortLink.origin}${shortLink.pathname}${query ? `?${query}` : ''}${shortLink.hash}`;
}

/** Short links only: a tenant link never passes through the worker, so nothing would strip it. */
export function withLinkPosition({
  url,
  position,
}: {
  url: string;
  position?: LinkPosition;
}): string {
  if (!position) return url;
  const shortLink = parseShortLink(url);
  if (!shortLink) return url;
  const kept = queryWithoutPosition(shortLink);
  const marker = `${LINK_POSITION_PARAM}=${position}`;
  return withQuery({ shortLink, query: kept ? `${kept}&${marker}` : marker });
}

export function withoutLinkPosition(url: string): string {
  const shortLink = parseShortLink(url);
  if (!shortLink?.searchParams.has(LINK_POSITION_PARAM)) return url;
  return withQuery({ shortLink, query: queryWithoutPosition(shortLink) });
}
